/**
 * 見積り乖離判定に関するユーティリティ
 */
import { CellClassParams } from 'ag-grid-community'
import { colorPalette } from '../../../style/colorPalette'
import {
  ProductBacklogItemRow,
  ProductBacklogRow,
  isProductBacklogItemRow,
  isUnsetSprintRowUuid,
  createSprintProductItemKey,
} from '../productBacklog'

// 小数点誤差を考慮した閾値
const VARIANCE_THRESHOLD = 0.001

// スプリントごとの見積り工数情報
export type SprintEstimatedHourInfo = {
  sprintUuid: string
  sprintName: string
  estimatedHour: number
}

// スプリントごとの見積り時間を取得（編集中のデータも考慮）
const getSprintEstimatedHour = (
  pbiRow: ProductBacklogItemRow,
  sprintUuid: string
): number => {
  const sprintProductItemKey = createSprintProductItemKey(sprintUuid)
  const editedValue = pbiRow.editedData?.[sprintProductItemKey]
  if (editedValue !== undefined) {
    return editedValue || 0
  }
  const sprintProductItem = pbiRow.sprintProductItems?.find(
    spi => spi.sprintUuid === sprintUuid
  )
  return sprintProductItem?.estimatedHour || 0
}

// 全スプリントの見積り時間合計を取得
const getTotalSprintEstimatedHour = (pbiRow: ProductBacklogItemRow): number => {
  const sprints = pbiRow.wbsItem.sprints || []
  return sprints.reduce(
    (total, sprint) => total + getSprintEstimatedHour(pbiRow, sprint.uuid),
    0
  )
}

// スプリント別の見積り工数リストを取得
export const getSprintEstimatedHourList = (
  pbiRow: ProductBacklogItemRow
): SprintEstimatedHourInfo[] => {
  const sprints = pbiRow.wbsItem.sprints || []
  return sprints.map(sprint => ({
    sprintUuid: sprint.uuid,
    sprintName: sprint.name,
    estimatedHour: getSprintEstimatedHour(pbiRow, sprint.uuid),
  }))
}

// 乖離情報を取得
export const getVarianceInfo = (pbiRow: ProductBacklogItemRow) => {
  const originalHour = pbiRow.wbsItem.estimatedWorkload?.hour ?? 0
  const sprintTotalHour = getTotalSprintEstimatedHour(pbiRow)
  const hasVariance =
    Math.abs(originalHour - sprintTotalHour) > VARIANCE_THRESHOLD
  const sprintBreakdown = getSprintEstimatedHourList(pbiRow)
  return { originalHour, sprintTotalHour, hasVariance, sprintBreakdown }
}

// 乖離判定対象の行データを取得（対象外ならnull）
export const getVarianceTargetRow = (
  data: ProductBacklogRow | undefined
): ProductBacklogItemRow | null => {
  if (!data || !isProductBacklogItemRow(data)) return null
  const parentSprintUuid = data.treeValue?.[0]
  if (!parentSprintUuid || isUnsetSprintRowUuid(parentSprintUuid)) return null
  return data
}

// 乖離がある場合の背景色スタイル
export const getVarianceCellStyle = (
  params: CellClassParams<ProductBacklogRow>
): { background: string } | undefined => {
  const row = getVarianceTargetRow(params.data)
  if (!row) return undefined
  if (getVarianceInfo(row).hasVariance) {
    return { background: colorPalette.pink[1] }
  }
  return undefined
}
